import { CameraAlt, Groups, House, InsertDriveFile, Room } from '@mui/icons-material';
import { Box, Button, Checkbox, Divider, FormControlLabel, Grid, Skeleton, Typography } from '@mui/material';
import { Form, Formik, FormikContextType } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Attachments from '../../components/Attachments/Attachments';
import BackButton from '../../components/Buttons/BackButton';
import { MediaSlides } from '../../components/Carousel/types';
import ConditionalWrapper from '../../components/ConditionalWrapper/ConditionalWrapper';
import Details from '../../components/Details/Details';
import DialogAction from '../../components/Dialog/DialogAction';
import TermsDialog from '../../components/Dialog/TermsDialog';
import { RequestStatus } from '../../components/dictionaries/RequestStatus';
import GMapsPlaceMarker from '../../components/Maps/GMapsPlaceMarker';
import LinesSkeleton from '../../components/Skeletons/LinesSkeleton';
import { VerticalStep, VerticalStepper } from '../../components/Stepper';
import { formatAzureFiles, formatMediaData, isSubmitEnabled, validateLocationCoordinates } from '../../lib/helperFuncs';
import { IntExt } from '../../lib/permissions';
import { CreateProfessionalValidation } from '../../lib/static/professionals/validationSchema';
import HttpService from '../../services/HttpService';
import UserService from '../../services/UserService';
import Theme from '../../Themes/main.theme';
import { useApiState } from '../../hooks/useApiState';
import { ApiUsability } from '../../lib/generalTypes';
import { renderErrors } from '../../components/form_v2/helpers';
import CarouselContainerForDetails from '../../components/Carousel/CarouselContainerForDetails';
import i18n from '../../i18n';

export interface IData {
  title: string;
  afm: string;
  category_level_1: string;
  services: string;
  languages: string[] | undefined
  regions: string; 
  show_on_map: 'visible' | 'hidden' | 'sample';
  city_name: string;
  tk: string;
  address: string;
  address_number: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  email: string;
  latitude: number | null;
  longitude: number | null;
  thumbnail: string;
  website?: string;
  facebook?: string;
  youtube?: string;
  linkedin?: string;
  instagram?: string;
  vimeo?: string;
  media: string[];
  attached_files: string[];
  resume: string;
  appl_applications: number
} 

const PreviewProfessional = (props: any) => {
  const { data, setRenderForm, isLoading } = props;

  const formRef = React.useRef<FormikContextType<any>>(null);

  const { images, videos, thumbnail } = 
  formatMediaData({
    media: data?.media || [],
    ...(data?.thumbnail && {thumbnail: data?.thumbnail})
  });  
  
  const [open, setOpen] = React.useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [checked, setChecked] = React.useState(false);
  const [termsOpen, setTermsOpen] = React.useState<boolean>(false);
  const { pathname } = useLocation();

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const {t} = useTranslation();

  const { apiEnabled, loadingApi } = useApiState(ApiUsability.Google_Maps, true);
  const {
    apiEnabled: geoLocationApi,
    loadingApi: geoLocationLoading
  } = useApiState(ApiUsability.Geolocation);

  const is_external = UserService.hasRole([IntExt.External]);

  const submitValues = async () => {
    try {
      const response = await HttpService.getAxiosClient().put(`${process.env.REACT_APP_BACKEND_URL}/applications/professionals/${data?.application_id}`, {
        status: RequestStatus.In_Progress
      });
      if (response.status === 200) {
        enqueueSnackbar(t('Η υποβολή αίτησης καταχώρισης επαγγελματία / επιχείρησης έγινε επιτυχώς!'), { variant: 'success' }); 
        if (pathname.split('/').filter(Boolean)[0] === 'admin') {
          navigate('/admin/applications/dashboard');
        } else navigate('/my-applications', { replace: true });
      }
    } catch (error) {
      enqueueSnackbar(t('Η υποβολή καταχώρισης αιτήματος επαγγελματία / επιχείρησης απέτυχε, παρακαλώ προσπαθήστε ξανά.'), { variant: 'error' });
      console.error(error);
    }
  };

  const basic_info_details = [
    {
      name: t('Επωνυμία Επαγγελματία / Επιχείρησης'),
      value: data?.title || '-',
      refValue: 'title'
    },
    {
      name: t('ΑΦΜ'),
      value: data?.afm || '-',
      refValue: 'afm'
    },
    {
      name: t('Κατηγορία Υπηρεσιών'),
      value: (data?.category_level_1 && data?.category_level_1?.filter(Boolean).length > 0)
        ? data?.category_level_1.map((category: any) => category.key)
        : '-',
      refValue: 'category_level_1'
    },
    {
      name: t('Κλάδος Ειδικότητας'),
      value: (data?.specialty && data?.specialty?.filter(Boolean).length > 0)
        ? data?.specialty.map((specialty: any) => specialty.key)
        : '-',
      refValue: 'specialty'
    },
    {
      name: t('Ειδικότητα Επαγγελματία'),
      value: (data?.specialty_lvl2 && data?.specialty_lvl2?.filter(Boolean).length > 0)
        ? data?.specialty_lvl2.map((specialty: any) => specialty.key)
        : '-',
      refValue: 'specialty_lvl2'
    },
    ...(is_external
      ? i18n.language !== "el" ? 
        [{
          name: t('Περιγραφή Προσφερόμενων Υπηρεσιών'),
          value: data?.services_en || '-',
          refValue: 'services_en'
        }]
        : [{
          name: t('Περιγραφή Προσφερόμενων Υπηρεσιών'),
          value: data?.services || '-',
          refValue: 'services'
        }]
      :[{
        name: t('Περιγραφή Προσφερόμενων Υπηρεσιών'),
        value: data?.services || '-',
        refValue: 'services'
      },
      {
        name: t('Περιγραφή Προσφερόμενων Υπηρεσιών'),
        value: data?.services || '-',
        refValue: 'services_en'
      }]),
    ...(is_external
      ? i18n.language === "el" ? 
        [{
          name: t('Παραγωγές που έχετε συμμετάσχει'),
          value: data?.productions || '-',
          refValue: 'productions' 
        }] :
        [{
          name: t('Παραγωγές που έχετε συμμετάσχει'),
          value: data?.productions_en || '-',
          refValue: 'productions_en'
        }] 
      : 
      [{
        name: t('Παραγωγές που έχετε συμμετάσχει'),
        value: data?.productions || '-',
        refValue: 'productions' 
      },
      {
        name: t('Παραγωγές που έχετε συμμετάσχει'),
        value: data?.productions_en || '-',
        refValue: 'productions_en'
      }]
    ),
    {
      name: t('Γλώσσες Επικοινωνίας'),
      value: (data?.languages && data?.languages?.length > 0)
        ? data?.languages.map((languages: any) => languages.key)
        : '-',
      refValue: 'languages'
    },
    {
      name: t('Περιοχές Εξυπηρέτησης / Παροχής Υπηρεσιών'),
      value: (data?.regions && data?.regions?.length > 0)
        ? data?.regions.map((regions: any) => t(regions.key))
        : '-',
      refValue: 'regions'
    }
  ];
  const basic_info_details_social = [
    [
      {
        name: t('Website'),
        value: data?.website || '-',
        refValue: 'website'
      },
      {
        name: t('Facebook'),
        value: data?.facebook || '-',
        refValue: 'facebook'
      },
      {
        name: t('YouTube'),
        value: data?.youtube || '-',
        refValue: 'youtube'
      }
    ],
    [
      {
        name: t('LinkedIn'),
        value: data?.linkedin || '-',
        refValue: 'linkedin'
      },
      {
        name: t('Instagram'),
        value: data?.instagram || '-',
        refValue: 'instagram'
      },
      {
        name: t('Vimeo'),
        value: data?.vimeo || '-',
        refValue: 'vimeo'
      }
    ]
  ];
  const address_info_details = [
    {
      name: t('Περιφέρεια / Δήμος'),
      value: t(data?.city_name) || '-',
      refValue: 'city'
    },
    {
      name: t('ΤΚ'),
      value: data?.tk
        ? String(data?.tk)
        : '-',
      refValue: 'tk'
    },
    {
      name: t('Οδός'),
      value: data?.address || '-',
      refValue: 'address'
    },
    {
      name: t('Αριθμός'),
      value: data?.address_number || '-',
      refValue: 'address_number'
    },
  ];
  const resp_usr_info_details = [
    {
      name: t('Όνομα'),
      value: data?.first_name || '',
      refValue: 'first_name'
    }, 
    {
      name: t('Επώνυμο'),
      value: data?.last_name || '',
      refValue: 'last_name'
    }, 
    {
      name: t('Τηλέφωνο'),
      value: data?.phone_number || '',
      refValue: 'phone_number'
    }, 
    {
      name: 'Email',
      value: data?.email || '',
      refValue: 'email'
    }
  ];
  const media_info_details = [
    {
      name: t('Φωτογραφία Εξωφύλλου'),
      value: data?.thumbnail,
      refValue: 'thumbnail'
    },
    {
      name: t('Φωτογραφίες/Βίντεο'),
      value: data?.media,
      refValue: 'media'
    },
    {
      name: t('Βιογραφικό Σημείωμα'),
      value: data?.resume,
      refValue: 'resume'
    },
    {
      name: t('Επισυναπτόμενα Έγγραφα'),
      value: data?.attached_files,
      refValue: 'attached_files'
    },
  ];
  const coordinates_details = [
    {
      name: t('Επιβεβαίωση Διεύθυνσης'),
      value: String(data?.latitude) || '-',
      refValue: 'location'
    }
  ];

  const basicInfo = <Box
    display='flex'
    justifyContent='flex-start'
  >
    <Box width='fit-content'>
      {isLoading
        ? <LinesSkeleton lines={basic_info_details.length} />
        : <Details
          removeLines
          rows={basic_info_details}
        />
      }
      <Box display='flex'
        flexDirection={{ md: 'row', xs: 'column'}}>
        {isLoading
          ? <LinesSkeleton lines={basic_info_details_social[0].length} />
          : basic_info_details_social.map((info) => {
            return (
              <Box key={JSON.stringify(info)}>
                <Details
                  removeLines
                  rows={info}
                />
              </Box>
            );
          })
        }
      </Box>
    </Box>
  </Box>;
  const addressInfo = <Box
    display='flex'
    justifyContent='flex-start'
    gap={15} >
    <Box sx={{width: 'fit-content'}}>
      {isLoading
        ? <LinesSkeleton lines={address_info_details.length} />
        : <Details
          removeLines
          rows={address_info_details}
        />
      }
    </Box>
  </Box>;
  const respUsrInfo = <Box
    display='flex'
    justifyContent='flex-start'
    gap={15} >
    <Box sx={{width: 'fit-content'}}>
      {isLoading
        ? <LinesSkeleton lines={resp_usr_info_details.length} />
        : <Details
          removeLines
          rows={resp_usr_info_details}
        />
      }
    </Box>
  </Box>;
  const mediaInfo = <CarouselContainerForDetails
    imageButtons={false}
    media={[
      ...(data?.thumbnail
        ? [ thumbnail ]
        : []), 
      ...images || [],
      ...videos || []] as MediaSlides[]} />;
  
  const filesInfo = <Box>
    {data?.resume
      ? <>
        <Typography fontWeight='500' marginLeft='16px'
          mt={2}>{t('Βιογραφικό Σημείωμα')}</Typography>
        <Attachments attachments={formatAzureFiles(data?.resume)} />
      </>
      : null}
    {(data?.attached_files && data?.attached_files?.length > 0)
      ? <>
        <Typography fontWeight='500' marginLeft='16px'
          mt={2}>{t('Άλλα Επισυναπτόμενα Έγγραφα')}</Typography>
        <Attachments attachments={formatAzureFiles(data?.attached_files)} />
      </>
      : null
    }
  </Box>;

  const steps = [
    {
      label: t('Βασικά Στοιχεία Επαγγελματία / Επιχείρησης'),
      content: basicInfo,
      icon: <House />
    },
    {
      label: t('Στοιχεία Έδρας Επιχείρησης'),
      content: <Grid container spacing={{xl: 38, xs: 1}}>
        <Grid item xs={12}
          md={5}>
          {addressInfo}
        </Grid>
        {(apiEnabled && !loadingApi) && (geoLocationApi && !geoLocationLoading)
          ? <Grid item xs={12}
            md>
            <ConditionalWrapper condition={isLoading}
              wrapper={(children) => <Skeleton>{children}</Skeleton>}>
              <Box sx={{width: '400px', height: '220px'}} 
              >
                <GMapsPlaceMarker
                  zoom={12}
                  location={data?.show_on_map === 'hidden' 
                    ? undefined
                    : {lat: Number(data?.latitude),
                      lng: Number(data?.longitude)}  
                  }
                />
              </Box>
            </ConditionalWrapper>
          </Grid>
          : null
        }
      </Grid>,
      icon: <Room />
    },
    {
      label: t('Στοιχεία Υπεύθυνου Επικοινωνίας'),
      content: respUsrInfo,
      icon: <Groups />
    },
    ...((data?.thumbnail || (data?.media && data?.media?.length > 0))
      ? [{
        label: t('Φωτογραφίες / Βίντεο'),
        content: <Box>
          {mediaInfo}
        </Box>,
        icon: <CameraAlt />
      }]
      : []),
    ...(((data?.attached_files && data?.attached_files?.length > 0) || data?.resume)
      ? [{
        label: t('Επισυναπτόμενα Έγγραφα'),
        content: <Box>
          {filesInfo}
        </Box>,
        icon: <InsertDriveFile />
      }]
      : [])
  ];

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  const handleClickOpen = () => {
    setTermsOpen(true);
  };

  (geoLocationApi && !geoLocationLoading) &&
    validateLocationCoordinates(formRef, data?.latitude, data?.longitude);  

  return (
    <>
      <Box mt={2} mb={2}>
        <BackButton onClick={() => {
          searchParams.delete('preview');
          pathname.split('/').filter(Boolean)[1] !== 'edit' &&
          searchParams.set('edit', 'true');
          setSearchParams(searchParams);
          setRenderForm(true);
        }}/>
      </Box>
      {is_external
        ? <Typography variant='h2' mb={2}
          color={Theme.labelColor?.main}>
          {t('Προεπισκόπηση Αίτηματος Καταχώρισης Επαγγελματία / Επιχείρησης')}
        </Typography>
        : <Typography variant='h2' mb={2}
          color={Theme.labelColor?.main}>
          {t('Προεπισκόπηση Εσωτερικού Αίτηματος Καταχώρισης Επαγγελματία / Επιχείρησης')}
        </Typography>}
      <Divider sx={{mb: 5, border: 1, color: Theme?.dividerColor?.main}}/>
      <VerticalStepper activeStep={0}>
        {steps.map(step => {
          return (
            <VerticalStep
              active={true}
              key={step.label}
              label={step.label}
              icon={step.icon}>
              {step.content}
            </VerticalStep>
          );
        })}
      </VerticalStepper>
      <Box mt={2} ml={2}>
        <FormControlLabel 
          control={
            <Checkbox 
              checked={checked}
              onChange={handleCheckboxChange}/>
          }
          label={
            <>
              <Button onClick={handleClickOpen}>{t('Αποδοχή όρων Χρήσης & Ορθότητας Στοιχείων')}</Button>
              <TermsDialog open={termsOpen} handleClose={() => setTermsOpen(false)}
                onAccept={() => {
                  setTermsOpen(false);
                  setChecked(true);
                }}
                onDecline={()=> setChecked(false)}/>
            </>
          }/>
      </Box>
      <Formik
        innerRef={formRef}
        enableReinitialize
        onSubmit={() => undefined}
        initialValues={{...data, location: ''}}
        validationSchema={CreateProfessionalValidation}
        validateOnBlur={false}
        validateOnMount={true}
      >
        {formik =>
          (<>
            <Box ml={2} mt={3}>
              {renderErrors(formik.errors, [
                ...basic_info_details,
                ...basic_info_details_social.flat(),
                ...address_info_details,
                ...resp_usr_info_details,
                ...media_info_details,
                ...coordinates_details
              ])}
            </Box>
            <Form>
              <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 5, gap: 2 }}>
                <Button variant='outlined' onClick={() => {
                  searchParams.delete('preview');
                  pathname.split('/').filter(Boolean)[1] !== 'edit' &&
                  searchParams.set('edit', 'true');
                  setSearchParams(searchParams);
                  setRenderForm(true);
                }}>
                  {t('Επιστροφή')}
                </Button>
                <Button 
                  variant='contained'
                  disabled={isSubmitEnabled(formik?.errors, true) ||
                    !checked ||
                    ((geoLocationApi && !geoLocationLoading)
                      ? !data?.latitude || !data?.longitude
                      : false  
                    )
                  }
                  onClick={() => setOpen(true)}>
                  {t('Υποβολή')}
                </Button>
              </Box>
            </Form>
          </>)}
      </Formik>
      <DialogAction open={open} handleClose={() => setOpen(false)}
        title={`${t('Επιβεβαίωση Υποβολής Αίτησης')}`} 
        content={t('Είστε σίγουρος ότι θέλετε να υποβάλετε την αίτηση καταχώρισης επαγγελματία / επιχείρησης;')}
        onAccept={() => {
          submitValues();
          setOpen(false);
        }}/>
    </>
  );
};

export default PreviewProfessional;